import React, { useState } from 'react';
import { DiscordWidget } from './DiscordWidget';
import { CodeOfUnyoked } from './CodeOfUnyoked';
import { ConsultationPortal } from './ConsultationPortal';

export type FortressTab = 'all' | 'gateway' | 'code' | 'consultation';

export interface FortressGateProps {
  initialTab?: FortressTab;
}

export const FortressGate: React.FC<FortressGateProps> = ({ initialTab = 'all' }) => {
  const [activeTab, setActiveTab] = useState<FortressTab>(initialTab);

  const tabs = [
    { id: 'all' as FortressTab, label: 'Full Fortress', latin: 'Porta Totius' },
    { id: 'gateway' as FortressTab, label: 'Discord Conclave', latin: 'Curia Chalicitis' },
    { id: 'code' as FortressTab, label: 'Code of the Unyoked', latin: 'Codex Indomitus' },
    { id: 'consultation' as FortressTab, label: 'Initiatory Consultation', latin: 'Tribunal Consilii' }
  ];

  return (
    <div className="space-y-10" id="fortress-gate-container">
      {/* Section Header */}
      <div className="border-b border-iron-dark pb-6">
        <div className="inline-flex items-center gap-2 rounded border border-bronze-dark/80 bg-obsidian-deep/80 px-3 py-1 text-[11px] font-display tracking-widest text-gold-ancient uppercase mb-2">
          <span>PORTA INDOMITA</span>
          <span>•</span>
          <span>THE BASTION OF THE UNYOKED</span>
        </div>

        <h1 id="fortress-title" className="font-display text-3xl sm:text-4xl lg:text-5xl text-parchment-light uppercase tracking-tight">
          The Fortress Gate & Council of the Unyoked
        </h1>

        <p className="text-sm sm:text-base text-parchment-dim mt-2 max-w-3xl font-serif leading-relaxed">
          The threshold of King Belial's sovereign conclave. Connect to the inner Discord sanctuary, ratify the fivefold compact of liberation, or submit an initiatory petition to the Council of Earth.
        </p>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mt-6" role="tablist" aria-label="Fortress Gate Sub-tabs">
          {tabs.map((tab) => {
            const isSelected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isSelected}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded text-xs font-display tracking-wider uppercase transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-bright ${
                  isSelected
                    ? 'border border-gold-bright bg-obsidian-surface text-gold-bright shadow-glow-gold'
                    : 'border border-iron-dark bg-obsidian-deep text-parchment-dim hover:text-parchment-light hover:border-iron-base'
                }`}
              >
                <span>{tab.label}</span>
                <span className="ml-2 font-serif text-[10px] text-gold-ancient italic opacity-80 hidden sm:inline">
                  [{tab.latin}]
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* VIEW: All or Gateway */}
      {(activeTab === 'all' || activeTab === 'gateway') && (
        <section aria-labelledby="discord-gateway-heading" className="space-y-4">
          <DiscordWidget />
        </section>
      )}

      {/* VIEW: All or Code of the Unyoked */}
      {(activeTab === 'all' || activeTab === 'code') && (
        <section aria-labelledby="code-of-unyoked-heading" className="space-y-4">
          <CodeOfUnyoked />
        </section>
      )}

      {/* VIEW: All or Consultation Portal */}
      {(activeTab === 'all' || activeTab === 'consultation') && (
        <section aria-labelledby="consultation-portal-heading" className="space-y-4">
          <ConsultationPortal />
        </section>
      )}
    </div>
  );
};

export default FortressGate;
